/**
 * Pure ordering helpers for DraggableCardGrid, kept out of the component so the
 * band-aware persistence logic is unit-testable without a DOM.
 *
 * A card belongs to a `section` (band). Cards only ever reorder WITHIN their
 * band; the effective render order is, per band in first-appearance order, the
 * saved order (present cards of that band only), with each card the saved order
 * has never seen placed right after the card it follows by default.
 * The flattened result is contiguous per band (band 0 keys, then band 1, ...),
 * which is what lets the drag handler move a card within its band by splicing
 * against its same-band neighbours in the flat array.
 */

export type CardMeta = {
  key: string
  section: number
}

export type BandedOrder = {
  /** Flat key order across all bands, contiguous per band, in render order. */
  flat: string[]
  /** Per-band ordered key lists, keyed by section index. */
  bands: Map<number, string[]>
}

export function computeBandedOrder(cards: CardMeta[], saved: string[]): BandedOrder {
  const sectionsInOrder: number[] = []
  cards.forEach((c) => {
    if (!sectionsInOrder.includes(c.section)) sectionsInOrder.push(c.section)
  })
  const bands = new Map<number, string[]>()
  const flat: string[] = []
  sectionsInOrder.forEach((s) => {
    const bandKeys = cards.filter((c) => c.section === s).map((c) => c.key)
    const bandSet = new Set(bandKeys)
    const ordered = saved.filter((k) => bandSet.has(k))
    bandKeys.forEach((key, i) => {
      if (ordered.includes(key)) return
      // A new card keeps its default place: after its natural predecessor, or
      // first when nothing precedes it.
      const before = bandKeys
        .slice(0, i)
        .reverse()
        .find((k) => ordered.includes(k))
      ordered.splice(before ? ordered.indexOf(before) + 1 : 0, 0, key)
    })
    bands.set(s, ordered)
    flat.push(...ordered)
  })
  return { flat, bands }
}
